/**
 * LiteYouTubeEmbed — Click-to-load YouTube player (C3).
 *
 * Why not just drop a regular <iframe>? A vanilla YouTube iframe pulls
 * ~600 KB of JS + cookies + auto-play probes on every page that mounts it.
 * On a property-detail page where most visitors will never hit play, that
 * tanks LCP and burns mobile data for no value.
 *
 * Instead we render:
 *   1. The free public thumbnail (`img.youtube.com/vi/{id}/maxresdefault.jpg`)
 *      with `loading="lazy"` so off-screen videos cost zero bytes.
 *   2. A play-button overlay.
 *
 * On the first user click we swap the placeholder for a real iframe pointing
 * at `youtube-nocookie.com` with `autoplay=1`, so the visitor lands directly
 * inside the playing video — no extra click required.
 *
 * Props:
 *   - videoId (required)       11-char YouTube ID
 *   - title (optional)         alt text + ARIA label
 *   - thumbnailUrl (optional)  override (defaults to YouTube's maxresdefault)
 *   - className (optional)     extra Tailwind classes on the wrapper
 */
import React, { useState } from 'react';
import { Play } from 'lucide-react';

const LiteYouTubeEmbed = ({ videoId, title, thumbnailUrl, className = '' }) => {
  const [playing, setPlaying] = useState(false);
  if (!videoId) return null;

  const thumb = thumbnailUrl || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const embed = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;

  return (
    <div
      className={`relative w-full overflow-hidden rounded-xl bg-black ${className}`}
      style={{ aspectRatio: '16 / 9' }}
      data-testid="lite-youtube-embed"
    >
      {!playing ? (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={title ? `Lire la vidéo : ${title}` : 'Lire la vidéo'}
          className="group absolute inset-0 w-full h-full focus:outline-none"
          data-testid="lite-youtube-play"
        >
          <img
            src={thumb}
            alt={title || 'Vignette vidéo YouTube'}
            loading="lazy"
            decoding="async"
            // YouTube sometimes returns a 120×90 placeholder for very recent
            // uploads; fall back to hqdefault which always exists.
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = '1';
                e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
              }
            }}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {/* Dim layer + centered play button */}
          <span className="absolute inset-0 bg-black/15 group-hover:bg-black/25 transition-colors" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex items-center justify-center h-16 w-16 rounded-full bg-red-600 group-hover:bg-red-700 shadow-lg ring-4 ring-white/30 transition-transform duration-200 group-hover:scale-110">
              <Play className="h-7 w-7 text-white ml-1" fill="currentColor" />
            </span>
          </span>
          {/* Top-left provider chip */}
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 text-stone-800 text-label font-semibold rounded-full px-2.5 py-1 shadow-sm">
            <Play className="h-3 w-3 text-red-600" fill="currentColor" />
            Visite vidéo
          </span>
        </button>
      ) : (
        <iframe
          src={embed}
          title={title || 'YouTube video player'}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      )}
    </div>
  );
};

export default LiteYouTubeEmbed;
